import React from 'react';
import {
    Activity,
    BadgeCheck,
    Bell,
    Building2,
    ChartBar,
    ChartColumn,
    ChartPie,
    CircleDollarSign,
    ClipboardCheck,
    ClipboardList,
    ClipboardPen,
    FileSignature,
    History,
    Layers,
    LayoutDashboard,
    ListChecks,
    MapPinned,
    Megaphone,
    MousePointerClick,
    Package,
    ReceiptText,
    Route,
    ScanBarcode,
    ShieldCheck,
    Sparkles,
    Store,
    Tags,
    UserCheck,
    Users,
    UsersRound
} from 'lucide-react';
import { t } from '../../../company/labels';
import { getActiveCompany } from '../../../company/session';

// Labels are read once per page load: the company only changes with a full reload (sign-in to
// another company, logout).

export const ClientList = [
    {
        label: 'Mi Empresa',
        key: 'my-client',
        icon: <Building2 />
    },
    {
        label: 'Pisos de Venta',
        key: 'stores',
        icon: <Store />
    },
    {
        label: t('promotores'),
        key: 'promoters',
        icon: <UsersRound />
    }
];

export const sidebarList = [
    {
        label: 'Inicio',
        key: 'dashboard',
        icon: <LayoutDashboard />
    },
    {
        label: 'Notificaciones',
        key: 'notifications',
        icon: <Bell />
    },
    {
        label: 'Clientes',
        key: 'clients',
        icon: <Building2 />
    },
    {
        label: 'Pisos de Venta',
        key: 'stores',
        icon: <Store />
    },
    {
        label: 'Usuarios',
        key: 'users',
        icon: <Users />
    },
    {
        // Admin-only (see PERMISSIONS_KEY in core/access.js).
        label: 'Permisos',
        key: 'permissions',
        icon: <ShieldCheck />
    },
    {
        label: 'Precio',
        key: 'precio',
        icon: <CircleDollarSign />,
        children: [
            {
                label: 'Lista de precio',
                key: 'price',
                icon: <ReceiptText />
            },
            {
                label: t('navDistributorPrices'),
                key: 'distributor-prices',
                icon: <ClipboardList />
            },
            {
                label: 'Levantamiento General',
                key: 'levantamiento-general',
                icon: <ScanBarcode />
            }
        ]
    },
    {
        label: 'Productos',
        key: 'products',
        icon: <Package />
    },
    {
        label: 'Segmentaciones',
        key: 'segments',
        icon: <Layers />
    },
    {
        label: 'Etiquetas',
        key: 'tags',
        icon: <Tags />
    },
    {
        label: 'Análisis SOS',
        key: 'sos-analysis',
        icon: <ChartPie />
    },
    {
        label: 'Interacciones App',
        key: 'user-interactions',
        icon: <MousePointerClick />
    },
    {
        label: 'Trade Marketing',
        key: 'trade-m',
        icon: <Megaphone />,
        children: [
            {
                label: t('promotores'),
                key: 'promoters',
                icon: <UsersRound />
            },
            {
                label: 'Auditores',
                key: 'auditors',
                icon: <UserCheck />
            },
            {
                label: 'Opciones de auditoría',
                key: 'auditor-field-options',
                icon: <ListChecks />
            },
            {
                label: 'Consola de auditoría',
                key: 'audit-console',
                icon: <ClipboardCheck />
            },
            {
                label: 'Aprobación de productos',
                key: 'product-approval',
                icon: <BadgeCheck />
            },
            {
                label: t('navPolls'),
                key: 'polls',
                icon: <ClipboardPen />
            },
            {
                label: 'Contrataciones',
                key: 'contracts',
                icon: <FileSignature />
            },
            {
                label: 'Campañas',
                key: 'campaigns',
                icon: <Sparkles />
            }
        ]
    },
    {
        label: 'Reportes',
        key: 'reports',
        icon: <ChartColumn />,
        children: [
            {
                label: 'Share of Shelf',
                key: 'share-of-shelf',
                icon: <ChartBar />
            },
            {
                label: 'Distribución Numérica',
                key: 'numeric-distribution',
                icon: <MapPinned />
            },
            {
                label: 'Uso de Shelf',
                key: 'uso-de-shelf',
                icon: <Activity />
            },
            {
                label: 'Eficiencia de Ruta',
                key: 'route-efficiency',
                icon: <Route />
            },
            {
                label: 'Registro de Actividad',
                key: 'activity-logs',
                icon: <History />
            }
        ]
    }
];

export const sidebarKeys = [
    {
        key: 'dashboard',
        href: '/dashboard'
    },
    {
        key: 'notifications',
        href: '/dashboard/notifications'
    },
    {
        key: 'clients',
        href: '/dashboard/clients'
    },
    {
        key: 'my-client',
        href: '/dashboard/clients/client'
    },
    {
        key: 'stores',
        href: '/dashboard/stores'
    },

    {
        key: 'promoters',
        href: '/dashboard/trade/promoters'
    },
    {
        key: 'auditors',
        href: '/dashboard/trade/auditors'
    },
    {
        key: 'auditor-field-options',
        href: '/dashboard/trade/auditor-field-options'
    },
    {
        key: 'audit-console',
        href: '/dashboard/trade/audit-console'
    },
    {
        key: 'product-approval',
        href: '/dashboard/trade/product-approval'
    },
    {
        key: 'contracts',
        href: '/dashboard/trade/contracts'
    },
    {
        key: 'campaigns',
        href: '/dashboard/trade/campaigns'
    },

    {
        key: 'users',
        href: '/dashboard/users'
    },
    {
        key: 'permissions',
        href: '/dashboard/permissions'
    },
    {
        key: 'price',
        href: '/dashboard/price'
    },
    {
        key: 'products',
        href: '/dashboard/products'
    },
    {
        key: 'segments',
        href: '/dashboard/segments'
    },
    {
        key: 'tags',
        href: '/dashboard/tags'
    },
    {
        key: 'sos-analysis',
        href: '/dashboard/sos-analysis'
    },
    {
        key: 'activity-logs',
        href: '/dashboard/activity-logs'
    },
    {
        key: 'user-interactions',
        href: '/dashboard/user-interactions'
    },
    {
        key: 'route-efficiency',
        href: '/dashboard/route-efficiency'
    },
    {
        key: 'distributor-prices',
        href: '/dashboard/distributor-prices'
    },
    {
        key: 'levantamiento-general',
        href: '/dashboard/levantamiento-general'
    },
    {
        key: 'polls',
        href: '/dashboard/trade/polls'
    },
    {
        key: 'uso-de-shelf',
        href: '/dashboard/reports/uso-de-shelf'
    },
    {
        key: 'share-of-shelf',
        href: '/dashboard/reports/share-of-shelf'
    },
    {
        key: 'numeric-distribution',
        href: '/dashboard/reports/numeric-distribution'
    }
];

/**
 * Sidebar items the signed-in company adds for its own screens (`navigation.extraNav` in
 * companies/<id>/navigation.js): { key, label, icon, href, after? }.
 */
export const extraNavItems = (getActiveCompany().navigation?.extraNav || []).map(({ key, label, icon }) => ({ key, label, icon }));
export const extraNavKeys = (getActiveCompany().navigation?.extraNav || []).map(({ key, href }) => ({ key, href }));

/**
 * `items` with each extra item placed right after the top-level item its `after` names, or at
 * the end when it names none (or one that isn't there). Pure.
 */
export function placeExtraNav(items, extra) {
    const result = [...items];
    for (const { after, key, label, icon } of extra) {
        const item = { key, label, icon };
        const index = after ? result.findIndex((entry) => entry.key === after) : -1;
        if (index === -1) result.push(item);
        else result.splice(index + 1, 0, item);
    }
    return result;
}

/** The sidebar of the signed-in company: the base list plus its extra items in place. */
export const companySidebarList = placeExtraNav(sidebarList, getActiveCompany().navigation?.extraNav || []);
